import { readSession } from '@/helpers/storage'
import type { ApiResponse } from '@/models/api'
import { api, readData } from '@/services/apiClient'

export interface FormDefinition {
  id: number
  formName: string
  formJson: string
  enabled: boolean
  isOrderForm: boolean
  dateModified: string
  hasPublishedVersion: boolean
  currentVersionId: number | null
  currentVersionNumber: number | null
  publishedFormJson: string | null
}

export interface StoredOrder {
  id: number
  customerId: string
  customerName: string | null
  formId: number
  formName: string | null
  formVersionId: number
  formVersionNumber: number
  orderJson: string
  formVersionJson: string | null
  renderSnapshotJson?: string | null
  status: string
  dateModified: string
  placedAt: string
  placedByName: string
  actionId: string | null
}

export function loadForms(): Promise<FormDefinition[]> {
  return api.get<ApiResponse<FormDefinition[]>>('/forms').then(({ data }) => readData(data, []))
}

export function loadFormDefinition(id: number): Promise<FormDefinition> {
  return api.get<ApiResponse<FormDefinition>>(`/forms/${id}`).then(({ data }) => readData(data, null as unknown as FormDefinition))
}

export function saveForm(input: { id?: number | null; formName: string; formJson: string; enabled?: boolean; isOrderForm?: boolean }): Promise<number> {
  return api.post<ApiResponse<number>>('/forms', {
    id: input.id ?? null,
    formName: input.formName,
    formJson: input.formJson,
    ...(input.enabled === undefined ? {} : { enabled: input.enabled }),
    isOrderForm: input.isOrderForm ?? true,
  }).then(({ data }) => readData(data, 0))
}

export function setFormEnabled(id: number, enabled: boolean): Promise<void> {
  return api.post<ApiResponse<null>>(`/forms/${id}/status`, { enabled }).then(({ data }) => { readData(data, null) })
}

export function publishForm(id: number): Promise<number> {
  return api.post<ApiResponse<number>>(`/forms/${id}/publish`).then(({ data }) => readData(data, 0))
}

export function deleteForm(id: number): Promise<void> {
  return api.delete<ApiResponse<null>>(`/forms/${id}`).then(({ data }) => { readData(data, null) })
}

const loadingOrders = new Map<string, Promise<StoredOrder>>()
export function invalidateLoadingOrders(ids?: number[]) {
  const session = readSession()
  const prefix = `${session?.businessId}:${session?.userId}:`
  for (const key of loadingOrders.keys()) {
    if (key.startsWith(prefix) && (!ids || ids.includes(Number(key.slice(prefix.length))))) loadingOrders.delete(key)
  }
}
export function loadStoredOrder(id: number): Promise<StoredOrder> {
  const session = readSession()
  const key = `${session?.businessId}:${session?.userId}:${id}`
  const existing = loadingOrders.get(key)
  if (existing) return existing
  const request = api.get<ApiResponse<StoredOrder>>(`/orders/${id}`).then(({ data }) => readData(data, null as unknown as StoredOrder))
    .finally(() => { if (loadingOrders.get(key) === request) loadingOrders.delete(key) })
  loadingOrders.set(key, request)
  return request
}

export function amendOrder(id: number, orderJson: string, expectedDateModified: string): Promise<void> {
  return api.put<ApiResponse<number>>(`/orders/${id}`, { orderJson, expectedDateModified })
    .then(({ data }) => { readData(data, 0) })
}
